import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, Copy, Terminal, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import PublicLayout from "../components/PublicLayout";

export default function DocsPage() {
    const { user } = useAuth();

    const copyCmd = (txt) => {
        navigator.clipboard.writeText(txt);
        toast.success("Command copied to clipboard");
    };

    return (
        <PublicLayout>

            <div className="max-w-6xl mx-auto px-6 py-12 grid lg:grid-cols-4 gap-12">
                {/* Sidebar Navigation */}
                <aside className="lg:col-span-1 space-y-6">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-[#52525B] font-bold mb-3">GETTING STARTED</div>
                        <ul className="space-y-2 text-xs">
                            <li><a href="#install-ps" className="text-[#00FF41] hover:underline flex items-center gap-1"><ChevronRight className="w-3 h-3"/> Windows Installation</a></li>
                            <li><a href="#install-mac" className="text-[#A1A1AA] hover:text-[#FAFAFA] hover:underline flex items-center gap-1"><ChevronRight className="w-3 h-3"/> macOS / Linux</a></li>
                            <li><a href="#cli-ref" className="text-[#A1A1AA] hover:text-[#FAFAFA] hover:underline flex items-center gap-1"><ChevronRight className="w-3 h-3"/> CLI Commands</a></li>
                        </ul>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-[#52525B] font-bold mb-3">SaaS OVERVIEW</div>
                        <ul className="space-y-2 text-xs">
                            <li><a href="#custom-domains" className="text-[#A1A1AA] hover:text-[#FAFAFA] hover:underline flex items-center gap-1"><ChevronRight className="w-3 h-3"/> Custom Domains</a></li>
                            <li><a href="#basic-auth" className="text-[#A1A1AA] hover:text-[#FAFAFA] hover:underline flex items-center gap-1"><ChevronRight className="w-3 h-3"/> Tunnel Credentials</a></li>
                        </ul>
                    </div>
                </aside>

                {/* Main Content Area */}
                <main className="lg:col-span-3 space-y-12">
                    
                    {/* Welcome */}
                    <section>
                        <div className="label-mono text-[#00FF41] mb-2">// docs / welcome</div>
                        <h1 className="text-3xl font-display font-black tracking-tight mb-4 uppercase">SynapseLink Core Documentation</h1>
                        <p className="text-sm text-[#A1A1AA] leading-relaxed">
                            SynapseLink provides secure, instant HTTP tunnels from your localhost to the internet. Connect local webhooks, stripe notifications, local apps, or web servers with zero configuration.
                        </p>
                    </section>

                    <hr className="border-[#18181B]" />

                    {/* Windows Install */}
                    <section id="install-ps" className="space-y-4">
                        <h2 className="text-lg font-bold uppercase text-[#FAFAFA] flex items-center gap-2">
                            <Terminal className="w-4 h-4 text-[#00FF41]" /> Windows PowerShell Installation
                        </h2>
                        <p className="text-xs text-[#A1A1AA] leading-relaxed">
                            Run this one-click command inside a PowerShell terminal to check for Node.js dependencies and install the standalone SynapseLink executable automatically:
                        </p>
                        <div className="bg-[#050505] border border-[#18181B] p-4 flex items-center justify-between font-mono text-xs text-[#A1A1AA]">
                            <code className="text-[#00FF41]">iwr -useb https://get.synapselink.cloud/install.ps1 | iex</code>
                            <button onClick={() => copyCmd("iwr -useb https://get.synapselink.cloud/install.ps1 | iex")} className="text-[#52525B] hover:text-[#00FF41]">
                                <Copy className="w-4 h-4" />
                            </button>
                        </div>
                    </section>

                    {/* macOS/Linux Install */}
                    <section id="install-mac" className="space-y-4">
                        <h2 className="text-lg font-bold uppercase text-[#FAFAFA] flex items-center gap-2">
                            <Terminal className="w-4 h-4 text-[#00FF41]" /> macOS / Linux Installation
                        </h2>
                        <p className="text-xs text-[#A1A1AA] leading-relaxed">
                            Install global CLI client packages using npm packages directly:
                        </p>
                        <div className="bg-[#050505] border border-[#18181B] p-4 flex items-center justify-between font-mono text-xs text-[#A1A1AA]">
                            <code className="text-[#00FF41]">npm install -g @synapselink/cli</code>
                            <button onClick={() => copyCmd("npm install -g @synapselink/cli")} className="text-[#52525B] hover:text-[#00FF41]">
                                <Copy className="w-4 h-4" />
                            </button>
                        </div>
                    </section>

                    <hr className="border-[#18181B]" />

                    {/* CLI Commands Reference */}
                    <section id="cli-ref" className="space-y-4">
                        <h2 className="text-lg font-bold uppercase text-[#FAFAFA]">CLI Reference Guidelines</h2>
                        <div className="border border-[#18181B] divide-y divide-[#18181B] bg-[#0A0A0A] text-xs">
                            <div className="grid grid-cols-1 md:grid-cols-2 p-3.5 gap-2 hover:bg-[#0F0F0F]">
                                <div className="font-bold text-[#00FF41]">synapselink login --key {"<api_key>"}</div>
                                <div className="text-[#A1A1AA]">Authenticate the CLI client. Stored under user folder configuration.</div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 p-3.5 gap-2 hover:bg-[#0F0F0F]">
                                <div className="font-bold text-[#00FF41]">synapselink {"<port>"}</div>
                                <div className="text-[#A1A1AA]">Proxies public domain HTTP request traffic to your target localhost port (e.g. 3000).</div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 p-3.5 gap-2 hover:bg-[#0F0F0F]">
                                <div className="font-bold text-[#00FF41]">synapselink {"<port>"} --subdomain {"<name>"}</div>
                                <div className="text-[#A1A1AA]">Forces connection to register on a specific subdomain (PRO tier required).</div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 p-3.5 gap-2 hover:bg-[#0F0F0F]">
                                <div className="font-bold text-[#00FF41]">synapselink logout</div>
                                <div className="text-[#A1A1AA]">Deletes stored CLI authentication credentials.</div>
                            </div>
                        </div>
                    </section>

                    <hr className="border-[#18181B]" />

                    {/* Custom Domains */}
                    <section id="custom-domains" className="space-y-4">
                        <h2 className="text-lg font-bold uppercase text-[#FAFAFA]">Custom Domains Configuration</h2>
                        <p className="text-xs text-[#A1A1AA] leading-relaxed">
                            Pro/Enterprise accounts can configure their own brand domains (e.g. <code className="text-[#00FF41]">api.mycompany.com</code>). 
                            To point your domain:
                        </p>
                        <ol className="list-decimal list-inside text-xs text-[#A1A1AA] space-y-2 pl-2">
                            <li>Go to your dashboard, select the tunnel settings tab.</li>
                            <li>Enter your custom domain in the domain box and save.</li>
                            <li>Configure a CNAME record at your DNS provider pointing your domain to: <code className="text-[#00FF41]">cname.synapselink.cloud</code>.</li>
                            <li>Click "Verify DNS" inside the SynapseLink settings panel to activate the gate.</li>
                        </ol>
                    </section>

                    {/* Basic Auth */}
                    <section id="basic-auth" className="space-y-4">
                        <h2 className="text-lg font-bold uppercase text-[#FAFAFA]">Basic Auth & Tunnel Security</h2>
                        <p className="text-xs text-[#A1A1AA] leading-relaxed">
                            Secure webhook ingress routes or test endpoints by enabling password protection. Once enabled, SynapseLink blocks all public requests with an HTTP 401 gate, prompting visitors for a valid credential passcode before forwarding traffic to your local server.
                        </p>
                    </section>

                </main>
            </div>
        </PublicLayout>
    );
}
